import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';
import { ZipContentValidator } from 'app/shared/utils/zip-content-validator.util';

export interface SendBackReviewDialogResult {
	comment: string;
	attachments: File[];
}

@Injectable({
	providedIn: 'root',
})
export class SendBackReviewDialogService {
	// Mirror of the server-side allow-list in IWorkflowService.AllowedReviewHistoryExtensions.
	// Front-end pre-validation prevents both the infinite loader (the request is never sent
	// when validation fails here) and the user uploading clearly unsafe types.
	private static readonly ALLOWED_EXTENSIONS = new Set([
		'.pdf',
		'.doc',
		'.docx',
		'.xls',
		'.xlsx',
		'.csv',
		'.txt',
		'.png',
		'.jpg',
		'.jpeg',
		'.zip',
	]);

	// HTML accept attribute — keeps the native picker filtered to the same allow-list.
	private static readonly ACCEPT_ATTR =
		'.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.zip,' +
		'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
		'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
		'text/csv,text/plain,image/png,image/jpeg,application/zip,application/x-zip-compressed';

	private static readonly MAX_ATTACHMENTS = 10;
	private static readonly MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

	private readonly html = `
<div class="send-back-review-body">
	<div class="send-back-field">
		<label class="send-back-label" for="sendBackComment">Comments <span class="send-back-required">*</span></label>
		<textarea id="sendBackComment" class="form-control send-back-textarea" rows="6" maxlength="4000" placeholder="Add reviewer comments"></textarea>
	</div>
	<div class="send-back-field send-back-field--attachments">
		<label class="send-back-label" for="sendBackAttachments">Attachments</label>
		<div class="fileinput fileinput-new input-group send-back-fileinput" data-provides="fileinput">
			<div class="form-control send-back-filename" data-trigger="fileinput">
				<i class="glyphicon glyphicon-file fileinput-exists"></i>
				<span class="send-back-filename-text"></span>
			</div>
			<span class="input-group-addon btn btn-default btn-file send-back-btn-file">
				<span class="fileinput-new">Select files</span>
				<span class="fileinput-exists">Change</span>
				<input id="sendBackAttachments" type="file" name="sendBackAttachments" multiple accept="${SendBackReviewDialogService.ACCEPT_ATTR}" />
			</span>
			<a href="#" class="input-group-addon btn btn-default fileinput-exists send-back-remove" data-dismiss="fileinput" hidden>Remove</a>
		</div>
		<small class="send-back-hint"><span class="send-back-selected-files">You can upload up to ${SendBackReviewDialogService.MAX_ATTACHMENTS} files (PDF, Word, Excel, CSV, TXT, or images).</span></small>
	</div>
</div>`.trim();

	async open(): Promise<SendBackReviewDialogResult | null> {
		const result = await Swal.fire({
			title: 'Send Back to Address Comments',
			html: this.html,
			icon: 'warning',
			width: 520,
			padding: '1.25rem',
			showCancelButton: true,
			confirmButtonText: 'Send Back',
			cancelButtonText: 'Cancel',
			customClass: {
				popup: 'send-back-review-sw',
				actions: 'send-back-review-actions',
				confirmButton: 'btn btn-warning send-back-confirm',
				cancelButton: 'btn btn-danger send-back-cancel',
			},
			buttonsStyling: false,
			didOpen: () => this.bindFileInputUi(),
			preConfirm: () => this.getValidatedPayload(),
		});

		return (result.value as SendBackReviewDialogResult) ?? null;
	}

	private bindFileInputUi(): void {
		const root = Swal.getHtmlContainer();
		if (!root) {
			return;
		}

		const input = root.querySelector<HTMLInputElement>('#sendBackAttachments');
		const removeBtn = root.querySelector<HTMLAnchorElement>('.send-back-remove');
		const updateUi = () => this.syncFileUi(root);

		input?.addEventListener('change', () => setTimeout(updateUi, 0));
		removeBtn?.addEventListener('click', (event) => {
			event.preventDefault();
			if (input) {
				input.value = '';
			}
			updateUi();
		});

		updateUi();
	}

	private async getValidatedPayload(): Promise<SendBackReviewDialogResult | null> {
		const commentInput = document.getElementById('sendBackComment') as HTMLTextAreaElement | null;
		const attachmentInput = document.getElementById('sendBackAttachments') as HTMLInputElement | null;
		const comment = commentInput?.value?.trim() ?? '';
		if (!comment) {
			Swal.showValidationMessage('Comment is required.');
			return null;
		}

		const attachments = Array.from(attachmentInput?.files ?? []);

		if (attachments.length > SendBackReviewDialogService.MAX_ATTACHMENTS) {
			Swal.showValidationMessage(`You can upload up to ${SendBackReviewDialogService.MAX_ATTACHMENTS} attachments.`);
			return null;
		}

		const invalidType = attachments.find((file) => !this.isAllowedFile(file));
		if (invalidType) {
			Swal.showValidationMessage(
				`Invalid file type "${invalidType.name}". Only PDF, Word, Excel, CSV, TXT, and image files are allowed.`
			);
			return null;
		}

		const oversized = attachments.find((file) => file.size > SendBackReviewDialogService.MAX_FILE_SIZE_BYTES);
		if (oversized) {
			Swal.showValidationMessage(`"${oversized.name}" exceeds the 10 MB limit.`);
			return null;
		}

		const restrictedNames = await this.collectRestrictedZipEntries(attachments);
		if (restrictedNames.length > 0) {
			Swal.showValidationMessage(
				`The ZIP archive contains restricted file types that are not allowed: ${restrictedNames.join(', ')}. ` +
					'ZIP archives must not contain executable or script files.'
			);
			return null;
		}

		return {
			comment,
			attachments,
		};
	}

	private isAllowedFile(file: File): boolean {
		const name = file?.name?.toLowerCase() ?? '';
		const dotIndex = name.lastIndexOf('.');
		if (dotIndex < 0) {
			return false;
		}
		const extension = name.slice(dotIndex);
		return SendBackReviewDialogService.ALLOWED_EXTENSIONS.has(extension);
	}

	private async collectRestrictedZipEntries(files: File[]): Promise<string[]> {
		const restricted: string[] = [];
		for (const file of files) {
			if (!(file?.name ?? '').toLowerCase().endsWith('.zip')) {
				continue;
			}
			const result = await ZipContentValidator.inspect(file);
			result.restrictedEntries.forEach((entry) => restricted.push(entry.name));
		}
		return restricted;
	}

	private syncFileUi(root: HTMLElement): void {
		const input = root.querySelector<HTMLInputElement>('#sendBackAttachments');
		const files = input?.files ? Array.from(input.files) : [];
		const names = files.map((file) => file.name);

		const nameSpan = root.querySelector<HTMLElement>('.send-back-filename-text');
		if (nameSpan) {
			nameSpan.textContent = files.length <= 1 ? names[0] ?? '' : `${files.length} files selected`;
			nameSpan.title = names.join(', ');
		}

		const selectedFilesText = root.querySelector<HTMLElement>('.send-back-selected-files');
		const defaultHint = `You can upload up to ${SendBackReviewDialogService.MAX_ATTACHMENTS} files (PDF, Word, Excel, CSV, TXT, or images).`;
		if (selectedFilesText) {
			if (files.length === 0) {
				selectedFilesText.textContent = defaultHint;
				selectedFilesText.title = '';
			} else if (files.length === 1) {
				// Keep the single-file name only in the input box.
				// Keep hint visible to reinforce multi-file support.
				selectedFilesText.textContent = defaultHint;
				selectedFilesText.title = '';
			} else {
				selectedFilesText.textContent = names.join(', ');
				selectedFilesText.title = names.join(', ');
			}
		}

		const wrap = root.querySelector<HTMLElement>('.send-back-fileinput');
		if (wrap) {
			wrap.classList.toggle('fileinput-new', files.length === 0);
			wrap.classList.toggle('fileinput-exists', files.length > 0);
		}

		root.querySelector<HTMLElement>('.send-back-remove')?.toggleAttribute('hidden', files.length === 0);
	}
}
